/**
 * Ruteo del PORTAL PÚBLICO — por path, no por hash.
 *
 * ## Por qué acá sí hay paths y en el portal privado no
 *
 * El portal interno usa `#/matches` a propósito (ver `lib/router.ts`): se
 * sirve como archivos estáticos desde cualquier lado y el hash no necesita
 * ninguna reescritura en el server. El portal público tiene el requisito
 * inverso y más fuerte: **la URL se imprime en el programa del partido, se
 * manda por WhatsApp y la indexa Google.** `liga.org/feva` es una dirección;
 * `liga.org/#/p/feva` es una dirección que nadie dicta por teléfono.
 *
 * El costo es un `try_files` en el server, y ya está puesto: `deploy/nginx.conf`
 * sirve `/portal/index.html` para cualquier path bajo `/portal/`, así que
 * `/portal/feva/resultados` ya cae en la app sin tocar el despliegue. En dev,
 * Vite hace lo mismo por defecto.
 *
 * ## La gramática
 *
 *   /{slug}                              portada: lo que se juega hoy
 *   /{slug}/resultados                   archivo de resultados
 *   /{slug}/competencia/{id}             fixture + tabla
 *   /{slug}/competencia/{id}/{pestaña}   pestaña ∈ fixture | posiciones
 *   /{slug}/partido/{matchId}            ficha con marcador y evolución
 *   /{slug}/widget/court/{matchId}       overlay para OBS
 *
 * ## Por qué el widget lleva el slug
 *
 * El competidor publica `widgets.volleystation.com/court/{matchId}` y la
 * tentación es copiar la forma tal cual. No se puede: **desde el multi-tenant,
 * un `matchId` no identifica un partido.** FeVA y ACLAV pueden tener las dos un
 * `final-2026` —hay un test del API que lo prueba— así que una URL sin
 * organización sería ambigua, y resolverla "buscando en todas" sería el agujero
 * que el aislamiento entero viene a evitar. Se conserva la forma `widget/court`
 * (es lo que un productor de TV espera tipear) con el slug adelante.
 *
 * Todo este módulo es PURO: entra un string, sale un objeto. Sin DOM.
 */

export const PUBLIC_COMPETITION_TABS = ['fixture', 'posiciones'] as const;
export type PublicCompetitionTab = (typeof PUBLIC_COMPETITION_TABS)[number];
export const DEFAULT_PUBLIC_COMPETITION_TAB: PublicCompetitionTab = 'fixture';

export type PublicRouteName = 'home' | 'results' | 'competition' | 'match' | 'widget' | 'unknown';

export interface PublicRoute {
  name: PublicRouteName;
  /** Organización del portal. Siempre presente: es el primer segmento. */
  slug: string;
  competitionId?: string;
  competitionTab?: PublicCompetitionTab;
  matchId?: string;
  /** Query ya parseada (el widget la usa para sus opciones de estilo). */
  query: Record<string, string>;
  /** Path canónico que produce esta ruta, SIN la base (para armar links). */
  path: string;
}

export function isPublicCompetitionTab(value: string): value is PublicCompetitionTab {
  return (PUBLIC_COMPETITION_TABS as readonly string[]).includes(value);
}

/**
 * Segmentos que NO pueden ser un slug de organización.
 *
 * `assets` es de Vite y los otros tres son del despliegue compartido
 * (`deploy/nginx.conf` sirve la captura y su service worker en la raíz). Sin
 * esta lista, abrir `/portal/assets/algo.js` con el archivo faltante mostraría
 * el portal público de una federación llamada "assets".
 */
const RESERVADOS: ReadonlySet<string> = new Set([
  'assets',
  'capture',
  'portal',
  'sw.js',
  'manifest.webmanifest',
  'icon.svg',
  'healthz',
  'api',
]);

/** Mismo patrón que `ORG_SLUG_PATTERN` de `@scout/core` (copiado: el portal no ejecuta el core). */
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

export function isPublicSlug(value: string): boolean {
  return SLUG_PATTERN.test(value) && !RESERVADOS.has(value);
}

/** `'?a=1&b=2'` → `{ a: '1', b: '2' }`. Tolera basura sin lanzar. */
function parseQuery(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  const clean = raw.startsWith('?') ? raw.slice(1) : raw;
  if (clean === '') return out;
  for (const part of clean.split('&')) {
    if (part === '') continue;
    const eq = part.indexOf('=');
    const key = eq === -1 ? part : part.slice(0, eq);
    const value = eq === -1 ? '' : part.slice(eq + 1);
    try {
      out[decodeURIComponent(key)] = decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      out[key] = value;
    }
  }
  return out;
}

/**
 * `pathname` del browser → ruta pública, o `null` si esta URL **no** es del
 * portal público (y entonces manda el portal privado con su hash).
 *
 * `base` es el `import.meta.env.BASE_URL` de Vite: `/` en desarrollo y
 * `/portal/` en el build del despliegue (ver `deploy/web.Dockerfile`). Entra
 * por parámetro y no se lee acá adentro para que el módulo siga siendo puro y
 * testeable en Node.
 */
export function parsePublicPath(
  pathname: string,
  base = '/',
  search = '',
): PublicRoute | null {
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  const withoutBase = pathname.startsWith(normalizedBase)
    ? pathname.slice(normalizedBase.length)
    : pathname.replace(/^\//, '');

  const segments = withoutBase
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s !== '')
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });

  const slug = segments[0];
  // Sin segmentos = la raíz del portal, que sigue siendo el portal PRIVADO
  // (`#/matches`). El público empieza cuando hay un slug.
  if (slug === undefined) return null;
  if (!isPublicSlug(slug)) return null;

  const query = parseQuery(search);
  const route = (rest: Partial<PublicRoute>): PublicRoute => ({
    name: 'unknown',
    slug,
    query,
    path: `/${segments.join('/')}`,
    ...rest,
  });

  const head = segments[1];
  if (head === undefined) return route({ name: 'home', path: publicHomePath(slug) });

  switch (head) {
    case 'resultados':
      return route({ name: 'results', path: publicResultsPath(slug) });

    case 'competencia': {
      const competitionId = segments[2];
      if (competitionId === undefined) return route({});
      const rawTab = segments[3];
      const competitionTab =
        rawTab !== undefined && isPublicCompetitionTab(rawTab)
          ? rawTab
          : DEFAULT_PUBLIC_COMPETITION_TAB;
      return route({
        name: 'competition',
        competitionId,
        competitionTab,
        path: publicCompetitionPath(slug, competitionId, competitionTab),
      });
    }

    case 'partido': {
      const matchId = segments[2];
      if (matchId === undefined) return route({});
      return route({ name: 'match', matchId, path: publicMatchPath(slug, matchId) });
    }

    case 'widget': {
      // `/widget/court/{matchId}`: la forma que un productor de streaming
      // espera tipear en el browser source de OBS.
      if (segments[2] !== 'court') return route({});
      const matchId = segments[3];
      if (matchId === undefined) return route({});
      return route({ name: 'widget', matchId, path: publicWidgetPath(slug, matchId) });
    }

    default:
      return route({});
  }
}

// ---------------------------------------------------------------------------
// Constructores de links (una sola definición de cada forma de URL)
// ---------------------------------------------------------------------------

const enc = encodeURIComponent;

export function publicHomePath(slug: string): string {
  return `/${enc(slug)}`;
}

export function publicResultsPath(slug: string): string {
  return `/${enc(slug)}/resultados`;
}

export function publicCompetitionPath(
  slug: string,
  competitionId: string,
  tab: PublicCompetitionTab = DEFAULT_PUBLIC_COMPETITION_TAB,
): string {
  const head = `/${enc(slug)}/competencia/${enc(competitionId)}`;
  return tab === DEFAULT_PUBLIC_COMPETITION_TAB ? head : `${head}/${tab}`;
}

export function publicMatchPath(slug: string, matchId: string): string {
  return `/${enc(slug)}/partido/${enc(matchId)}`;
}

export function publicWidgetPath(slug: string, matchId: string): string {
  return `/${enc(slug)}/widget/court/${enc(matchId)}`;
}

/**
 * Path canónico → `href` real, con la base del despliegue adelante.
 *
 * Los links del portal público son `<a href>` de verdad (no `#`) para que el
 * click derecho → "abrir en pestaña nueva", el botón de atrás y el copiado de
 * URL funcionen como en cualquier sitio. La navegación la intercepta
 * `PublicApp` con `history.pushState`.
 */
export function publicHref(path: string, base = '/'): string {
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${normalizedBase}${path}`;
}
